/**
 * Copyright IBM Corp. 2022
 *
 * This source code is licensed under the IBM Public License Version 1.0 found in the
 * LICENSE file in the root directory of this source tree.
 */
import memo from "memoize-one";
import TriFloorPlanPlugin from "./TriFloorPlanPlugin.js";
import TriFloorPlanUtils from "../util/TriFloorPlanUtils.js";

export default class TriDropablePlugin extends TriFloorPlanPlugin
{
  static type = "Dropable";

  /**
   *Properties
   * format
   *    An array of supported formats.  Each entry describes a MIME type
   *    accepted by the Dropable plugin.  Eache entry has the following
   *    properties:
   *    dropEffect
   *      The drop effect assigned to the dragover event for dropable elements.
   *      "none" is assigned for non-dropable elements or if this value is not
   *      specified.  This value can be overridden for individual elements by
   *      the onDragover callback
   *    
   *    dropable
   *      A list of element ids that are valid drop targets. The format is:
   *      [ { [TriSelectablePlugin.SPACE_ID]: space.elementName } ]
   *    
   *    type
   *      The expected MIME format of the data.  If not specified text/plain
   *      is used.  If application/json is specified, the plug-in attempts
   *      to parse the JSON and pass the resulting object to onDrop
   * 
   *    onDrop( id, data, MIMEType )
   *      Called when the drop event is recieved for a dropable element
   *      - id: Id of target element
   *      - data: data for the specified MIME format if available or null
   *      - MIMEType:  MIME type of the dropped date
   *
   *    onDragover( id )
   *      Callback called when the dragover event is recieved for a dropable
   *      element.  It is passed the element Id.  It is expected to return
   *      the drag effect for that element. If it is not specified, the drag
   *      effect for the format is used if specified, 
   *    
   */ 

    memoizedDropableById = memo( ( dropable ) =>
        this.computeDropableById( dropable )
    );

    get dropableById()
    {
        return this.memoizedDropableById( this.props.dropable );
    }

    pluginDidUpdate()
    {
        const { floorPlanLoaded, enabled } = this.props;
        if( !floorPlanLoaded || !enabled )
        {
            this.stop();
            return;
        }
        this.start();

      if( this.props.formats )
      {
        this.formatMap = new Map();
        this.formats = this.props.formats;
        if( !Array.isArray( this.formats ) ) this.formats = [ this.formats ];
        this.formats.forEach( format =>
        {
          format.dropableMap = this.computeDropableById( format.dropable );
          this.formatMap.set( format.type, format );
        } );
      }
      else
      {
        this.formatMap = new Map();
        this.formats = [];
      }
    }

    pluginDidRemove()
    {
        this.stop();
    }

    start()
    {
        this.addSvgDropListener();

        if( this.props.dropable !== this.lastDropable )
        {
            this.lastDropable = this.props.dropable;
        }
    }

    stop()
    {
        this.lastDropable = [];
        const { floorPlanLoaded } = this.props;
        if( !floorPlanLoaded ) return;

        this.removeSvgDropListener();
    }

    computeDropableById( dropable )
    {
        if( dropable )
        {
            const dropableById = new Map();
            dropable.forEach( ( item ) =>
            {
                dropableById.set(
                    TriFloorPlanUtils.getPropValue( item, TriDropablePlugin.SPACE_ID ),
                    item
                );
            } );
            return dropableById;
        }
        else
        {
            return null;
        }
    }

    addSvgDropListener()
    {
        const svgElement = this.props.triFloorPlan.svgElement;
        if( svgElement !== this.lastSvgElement )
        {
            if( svgElement != null )
            {
                svgElement.addEventListener( "dragover", this.handleDragover );
                svgElement.addEventListener( "drop", this.handleDrop );
            }
            this.lastSvgElement = svgElement;
        }
    }

    removeSvgDropListener()
    {
        const svgElement = this.props.triFloorPlan.svgElement;
        if( svgElement != null && svgElement === this.lastSvgElement )
        {
          svgElement.removeEventListener( "dragover", this.handleDragover );
          svgElement.removeEventListener( "drop", this.handleDrop );
        }
        this.lastSvgElement = null;
    }

    lookupFormat( evt )
    {
        const types = evt.dataTransfer.types;
        let format;
        for( let i = 0; i < types.length; i++ )
        {
            format = this.formatMap.get( types[ i ] );
            if( format ) break;
        }
        return format;
    }

    handleDragover = ( evt ) =>
    {
        evt.preventDefault();

        const { floorPlanLoaded, enabled } = this.props;
        if( !enabled || !floorPlanLoaded ) return;
        if( !this.formatMap )
        {
            evt.dataTransfer.dropEffect = "none";
            return;
        }

        const idproperty = this.props.triFloorPlan.props.spaceLayer
                          ? TriDropablePlugin.EMELEMENT_PROPERTY
                          : TriDropablePlugin.NAME_PROPERTY;

        const recordId = evt.target.getAttribute( idproperty );
        if( !recordId )
        {
            evt.dataTransfer.dropEffect = "none";
            return;
        }

        const format = this.lookupFormat( evt );
        if( !format || !format.dropableMap )
        {
            evt.dataTransfer.dropEffect = "none";
            return;
        }

        if( !format.dropableMap.has( recordId ) ) 
        {
            evt.dataTransfer.dropEffect = "none";
            return;
        }

        let { dropEffect } = format;
        if( format.onDragover )
        {
            dropEffect = onDragover( recordId, format.type );
        }
        if( !dropEffect || dropEffect === "" ) dropEffect = "none";
        evt.dataTransfer.dropEffect = dropEffect;
    }

    handleDrop = ( evt ) =>
    {
        evt.preventDefault();

        const { floorPlanLoaded, enabled } = this.props;
        if( !enabled || !floorPlanLoaded ) return;

        const format = this.lookupFormat( evt );
        if( !format || !format.onDrop ) return;

        const idproperty = this.props.triFloorPlan.props.spaceLayer
          ? TriDropablePlugin.EMELEMENT_PROPERTY
          : TriDropablePlugin.NAME_PROPERTY;

        const recordId = evt.target.getAttribute( idproperty );
        if( !(    recordId
               && format.dropableMap
               && format.dropableMap.has( recordId ) ) ) return;

        let type = format.type;
        if( !type || type === "" ) type = "text/plain";

        let data = evt.dataTransfer.getData( type );
        if( !data && type !== "text/plain" )
        {
            type = "text/plain";
            data = evt.dataTransfer.getData( type );
        }

        if( !data ) return;
        if( format.type === "application/json" )
        {
            data = JSON.parse( data );
        }

        const keys = {
            altKey: evt.altKey,
            ctrlKey: evt.ctrlKey,
            shiftKey: evt.shiftKey,
        }
        format.onDrop( recordId, data, type, keys );
    }
}
